/**
 * «Lo que sabemos» — GEN-EKB-019. Rejilla de áreas (GEN-PX-032: una tarjeta por área, raíz o subárea, ya en el orden de uso del kernel) con sus KBs: etiqueta de color, titular,
 * n.º de iKus vigentes y aviso de vigencia. Mockup: Saber-1-mapa.html (.areas, .area, .kb, .et, .alert).
 * El kernel decide qué KBs salen (`mapa` de GET /v1/ekb/saber); aquí solo se pinta. «paquete» queda fuera (E15).
 */
import { useState } from 'react'
import { Avatar } from '../despacho/ui.jsx'

const MAX_AREAS = 6
const KBS_POR_AREA = 3 // GEN-PX-031: la portada enseña los 3 más recientes de cada área (el kernel ya los manda por fecha, GEN-PX-030); el resto, tras «Ver los N KBs →»
const ETIQUETA = { confidencial: ['Confidencial', 'conf'], interna: ['Interna', 'int'], publica: ['Pública', 'pub'] }

export const plural = (n, uno, varios) => `${Number(n).toLocaleString('es-ES', { useGrouping: 'always' })} ${n === 1 ? uno : varios}`

function iniciales(nombre) {
  const base = (nombre ?? '').split('@')[0].trim()
  const palabras = base.split(/[\s._-]+/).filter(Boolean)
  const letras = palabras.length > 1 ? palabras[0][0] + palabras[1][0] : base.slice(0, 2)
  return letras.toUpperCase()
}

function mesDe(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('es-ES', { month: 'long' })
}

/** GEN-PX-030/031: «hoy», «ayer», «hace 3 días», «hace 2 meses»… y el punto de frescura (verde < 1 mes · ámbar 1-3 meses · gris > 3). */
export function frescura(iso, ahora = Date.now()) {
  const t = Date.parse(iso ?? '')
  if (Number.isNaN(t)) return null
  const dias = Math.max(0, Math.floor((ahora - t) / 86400000))
  const texto = dias === 0 ? 'hoy' : dias === 1 ? 'ayer' : dias < 30 ? `hace ${dias} días`
    : dias < 365 ? `hace ${Math.floor(dias / 30)} ${Math.floor(dias / 30) === 1 ? 'mes' : 'meses'}`
      : `hace ${Math.floor(dias / 365)} ${Math.floor(dias / 365) === 1 ? 'año' : 'años'}`
  return { texto, nivel: dias < 30 ? 'verde' : dias <= 90 ? 'ambar' : 'gris' }
}

export function FilaKb({ kb, onAbrir }) {
  const [texto, clase] = ETIQUETA[kb.etiqueta] ?? [kb.etiqueta, 'int']
  const nombreTitular = kb.titular?.display_name ?? kb.titular?.email ?? ''
  const fresca = frescura(kb.actualizado)
  return (
    <button type="button" className="dk-kb" onClick={() => onAbrir(kb)}>
      <span className="dk-kb__t">{kb.nombre}</span>
      <span className={`dk-et dk-et--${clase}`}>{texto}</span>
      <span className="dk-kb__m">
        {nombreTitular && (
          <>
            <Avatar iniciales={iniciales(nombreTitular)} nombre={nombreTitular} tamano={16} />
            {nombreTitular.split(' ')[0]} ·{' '}
          </>
        )}
        {plural(kb.ikus, 'iKu', 'iKus')}
        {fresca && (
          <span className="dk-kb__fresca" title={`Última actividad: ${fresca.texto}`}>
            {' · '}<i className={`dk-fresca dk-fresca--${fresca.nivel}`} aria-hidden="true" />{fresca.texto}
          </span>
        )}
        {/* GEN-EKB-044: en su área principal, dónde más se tiene en cuenta; en una secundaria, de qué área es. No cambia quién lo ve. */}
        {kb.tambien_en && kb.area_principal && (
          <span className="dk-kb__tambien" title={`Se tiene en cuenta en esta área. Su área principal es ${kb.area_principal.nombre}.`}>
            {' · '}<span aria-hidden="true">🔗</span> también en esta área · principal: {kb.area_principal.nombre}
          </span>
        )}
        {kb.tambien_en_areas?.length > 0 && (
          <span className="dk-kb__tambien" title="Áreas donde también se tiene en cuenta">
            {' · '}<span aria-hidden="true">🔗</span> también en {kb.tambien_en_areas.map(a => a.nombre).join(', ')}
          </span>
        )}
        {kb.caducan > 0 && <span className="dk-kb__alert"> · {kb.caducan} {kb.caducan === 1 ? 'caduca' : 'caducan'} en {mesDe(kb.caducan_desde)}</span>}
      </span>
    </button>
  )
}

export function FilaKbGestion({ k, areaId, onAbrirKb, gestion }) {
  return (
    <div className="dk-kb__fila">
      <FilaKb kb={k} onAbrir={onAbrirKb} />
      {gestion && !k.tambien_en && (
        <button type="button" className="dk-btn dk-kb__mover" title="Mover de área" aria-label="Mover de área"
          onClick={() => gestion.abrir('kb', { kb: { id: k.id, nombre: k.nombre, areaActual: areaId } })}>⇄</button>
      )}
    </div>
  )
}

/** KBs directos del área. `limite`: máx. de KBs (la portada); sin él, todos (la página del área). */
export function ContenidoArea({ area, limite = 0, onAbrirKb, gestion }) {
  const kbs = limite ? area.kbs.slice(0, limite) : area.kbs
  return (
    <>
      {area.kbs.length === 0 && <p className="dk-area__vacia">Sin KBs todavía</p>}
      {kbs.map(k => <FilaKbGestion key={k.id} k={k} areaId={area.id} onAbrirKb={onAbrirKb} gestion={gestion} />)}
    </>
  )
}

/** GEN-PX-032: «Equipo ›» encima del nombre. Con `onIr`, cada ancestro es un enlace a su página (`ruta` son los nombres; `ids`, los suyos). */
export function Ruta({ ruta, ids, onIr }) {
  if (!ruta?.length) return null
  return (
    <span className="dk-area__ruta">
      {ruta.map((n, i) => (
        <span key={ids?.[i] ?? n}>
          {onIr && ids?.[i] ? <button type="button" onClick={() => onIr(ids[i])}>{n}</button> : n}{' › '}
        </span>
      ))}
    </span>
  )
}

/**
 * GEN-PX-020f: acciones del área, en su página. `gestion` = { abrir, permisos, sistema: Set de ids de áreas de sistema, padres } solo para el
 * administrador (reutiliza el formulario de AreasPanel); `acciones` = { kbEn, saberEn, saberEnDisponible }. El kernel revalida cada operación.
 */
export function AccionesArea({ area, gestion = null, acciones = {} }) {
  const noSistema = gestion && !gestion.sistema.has(area.id)
  const hayAltas = noSistema || acciones.kbEn || (acciones.saberEn && acciones.saberEnDisponible?.(area))
  return (
    <>
      {gestion && (
        <div className="dk-area__gestion" aria-label={`Gestionar el área ${area.nombre}`}>
          {noSistema && <button type="button" className="dk-btn" onClick={() => gestion.abrir('editar', { area })}>Renombrar / icono</button>}
          {noSistema && <button type="button" className="dk-btn" onClick={() => gestion.abrir('mover', { area: { ...area, padre_id: gestion.padres.get(area.id) ?? null } })}>Mover</button>}
          {gestion.permisos && <button type="button" className="dk-btn" onClick={() => gestion.permisos(area)}>Permisos</button>}
          {noSistema && <button type="button" className="dk-btn" onClick={() => gestion.abrir('retirar', { area })}>Retirar</button>}
        </div>
      )}
      {hayAltas && (
        <div className="dk-area__gestion" aria-label={`Añadir en el área ${area.nombre}`}>
          {noSistema && <button type="button" className="dk-btn" onClick={() => gestion.abrir('nueva', { padre: area.id })}>+ Subárea</button>}
          {acciones.kbEn && <button type="button" className="dk-btn" onClick={() => acciones.kbEn(area)}>+ KB</button>}
          {acciones.saberEn && acciones.saberEnDisponible?.(area) && <button type="button" className="dk-btn dk-btn--primario" onClick={() => acciones.saberEn(area)}>+ Añadir saber</button>}
        </div>
      )}
    </>
  )
}

// GEN-PX-020f: las tarjetas del mapa no llevan botones de gestión ni de alta: viven en la página del área (AccionesArea).
export default function SaberMapa({ mapa, resumen, onAbrirKb, onVerArea, acciones = {} }) {
  const [todas, setTodas] = useState(false)
  const visibles = todas ? mapa : mapa.slice(0, MAX_AREAS)
  return (
    <section className="dk-mapa" aria-label="Lo que sabemos">
      <div className="dk-mapa__cab">
        <h2>Lo que sabemos</h2>
        <span className="dk-mapa__n">{plural(resumen.areas, 'área', 'áreas')} · {plural(resumen.kbs, 'KB', 'KBs')}</span>
        {/* GEN-EKB-020 (.sec-h .acts): cada botón solo si quien mira puede usarlo; el kernel revalida */}
        <div className="dk-mapa__acts">
          {acciones.area && <button type="button" className="dk-btn" onClick={acciones.area}>+ Área</button>}
          {acciones.permisos && <button type="button" className="dk-btn" onClick={acciones.permisos}>Permisos de KBs</button>}
          {acciones.kb && <button type="button" className="dk-btn" onClick={acciones.kb}>+ KB</button>}
          {acciones.saber && <button type="button" className="dk-btn dk-btn--primario" onClick={acciones.saber}>+ Añadir saber</button>}
        </div>
      </div>
      {/* GEN-PX-022: estado vacío de quien no tiene ningún KB permitido; el kernel nunca manda KBs no permitidos (GEN-EKB-021). */}
      {mapa.length === 0 && (
        <p className="dk-saber__aviso" role="status">
          Todavía no hay KBs que puedas ver. Cuando alguien te dé acceso a uno, aparecerá aquí; mientras tanto, pregúntale a Jimmy.
        </p>
      )}
      <div className="dk-areas">
        {visibles.map(a => (
          <div key={a.id} className="dk-area">
            <button type="button" className="dk-area__cab dk-area__cab--enlace" onClick={() => onVerArea?.(a.id)} aria-label={`Abrir el área ${a.nombre}`}>
              <div className="dk-area__ico" aria-hidden="true">{a.icono}</div>
              <div>
                <div className="dk-rotulo">Área</div>
                <Ruta ruta={a.ruta} />
                <h3>{a.nombre}</h3>
                <small>{a.subareas > 0 ? plural(a.subareas, 'subárea', 'subáreas') : a.padre_id ? 'subárea' : 'área raíz'}</small>
              </div>
              <span className="dk-area__tot">{plural(a.ikus, 'iKu', 'iKus')}</span>
            </button>
            {/* GEN-BUG-010: el kernel manda al admin y al champion también las áreas vacías */}
            <ContenidoArea area={a} limite={KBS_POR_AREA} onAbrirKb={onAbrirKb} />
            {(a.kbs_total ?? a.kbs.length) > KBS_POR_AREA && (
              <button type="button" className="dk-area__ver" onClick={() => onVerArea?.(a.id)}>
                <span>Ver los {a.kbs_total ?? a.kbs.length} KBs →</span>
                <small>{(a.kbs_total ?? a.kbs.length) - KBS_POR_AREA} más</small>
              </button>
            )}
          </div>
        ))}
      </div>
      {mapa.length > MAX_AREAS && (
        <button type="button" className="dk-mapa__mas" onClick={() => setTodas(t => !t)}>
          {todas ? 'ver menos' : `ver las ${mapa.length} áreas`}
        </button>
      )}
    </section>
  )
}
